import { useEffect, useState } from 'react';
import { QuoteContent } from '../type';
import { generateQuote } from './QuoteAPI';
import { useEditedWidget } from 'features/Rooms/PersonalMeetingCorner/states/hooks';

interface QuoteState {
    randomQuote: string;
    randomAuthor: string;
    customQuote: string;
    setCustomQuote: (quote: string) => void;
    customAuthor: string;
    setCustomAuthor: (author: string) => void;
    quoteCategory: string;
    setQuoteCategory: (category: string) => void;
    isGenerating: boolean;
    error: string;
    setError: (error: string) => void;
    generateQuotes: () => Promise<void>;
    useRandomQuote: () => void;
}

export const useQuoteState = (onQuoteSelect: (content: QuoteContent) => void): QuoteState => {
    const [randomQuote, setRandomQuote] = useState('');
    const [randomAuthor, setRandomAuthor] = useState('');
    const [customQuote, setCustomQuote] = useState('');
    const [customAuthor, setCustomAuthor] = useState('');
    const [quoteCategory, setQuoteCategory] = useState('inspirational');
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const [editedWidget] = useEditedWidget();
    const isEditMode = editedWidget?.content?.quote && editedWidget?.content?.author;

    const generateQuotes = async () => {
        setIsGenerating(true);
        setError('');
        try {
            const quoteData = await generateQuote(quoteCategory);
            setRandomQuote(quoteData.quote);
            setRandomAuthor(quoteData.author);
        } catch (error) {
            console.error('Error generating quote:', error);
            setError('Failed to generate quote. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    const useRandomQuote = () => {
        setCustomQuote(randomQuote);
        setCustomAuthor(randomAuthor);
    };

    useEffect(() => {
        const trimmedQuote = customQuote.trim();
        const trimmedAuthor = customAuthor.trim();

        if (trimmedQuote && trimmedAuthor) {
            onQuoteSelect({ quote: trimmedQuote, author: trimmedAuthor });
        } else {
            onQuoteSelect({});
            setError(''); // clear error, otherwise it will show error when user click on quote widget tab at first time
        }
    }, [customQuote, customAuthor, onQuoteSelect]);

    useEffect(() => {
        if (isEditMode) {
            setCustomQuote(editedWidget?.content?.quote || '');
            setCustomAuthor(editedWidget?.content?.author || '');
        }
    }, [editedWidget?.content]);

    return {
        randomQuote,
        randomAuthor,
        customQuote,
        setCustomQuote,
        customAuthor,
        setCustomAuthor,
        quoteCategory,
        setQuoteCategory,
        isGenerating,
        error,
        setError,
        generateQuotes,
        useRandomQuote
    };
};
